/**
 * SavedViewModal — J7-W5 task 6c-2.
 *
 * Small create / edit modal for `roros_saved_views`. Captures `name` +
 * `is_shared` toggle. The current filter + sort are passed in as props
 * so the parent (ControlTowerPage) decides when "current" means "the
 * filter chips above" vs "the filter pinned by an existing saved view
 * being edited".
 */

import React, { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Alert } from '@/components/ui/Alert';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import {
  useRorosSavedViewCreate,
  useRorosSavedViewUpdate,
} from '@/hooks/roros/control-tower';
import type { RorosSavedViewDTO } from '@/types/roros/saved-views';

interface SavedViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing view to edit. When omitted, the modal is in "create" mode. */
  view?: RorosSavedViewDTO | null;
  /** The current filter to capture in the new view. */
  currentFilter: Record<string, string>;
  currentSortBy?: string | null;
  currentSortOrder?: 'asc' | 'desc' | null;
  onSaved?: (view: RorosSavedViewDTO) => void;
}

export function SavedViewModal({
  isOpen,
  onClose,
  view,
  currentFilter,
  currentSortBy = null,
  currentSortOrder = null,
  onSaved,
}: SavedViewModalProps) {
  const isEdit = !!view;
  const [name, setName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useRorosSavedViewCreate();
  const update = useRorosSavedViewUpdate();
  const isPending = create.isPending || update.isPending;

  // Reset form whenever the modal opens. Edits hydrate from the view; new
  // views start empty.
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setName(view?.name ?? '');
    setIsShared(view?.isShared ?? false);
  }, [isOpen, view?.id, view?.name, view?.isShared]);

  async function handleSubmit() {
    if (!name.trim()) {
      setError(t('View name'));
      return;
    }
    try {
      let saved: RorosSavedViewDTO;
      if (isEdit && view) {
        saved = await update.mutateAsync({
          id: view.id,
          payload: {
            name: name.trim(),
            isShared,
            // When editing the view's stored filter, keep what's already
            // there; when the user explicitly wants to overwrite, the
            // ControlTowerPage will pass `currentFilter` matching the view.
            filter: currentFilter,
            sortBy: currentSortBy,
            sortOrder: currentSortOrder,
          },
        });
        showToast.success(t('View updated'));
      } else {
        saved = await create.mutateAsync({
          scope: 'control_tower',
          name: name.trim(),
          filter: currentFilter,
          sortBy: currentSortBy,
          sortOrder: currentSortOrder,
          isShared,
        });
        showToast.success(t('View saved'));
      }
      onSaved?.(saved);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? t('Failed to save view'));
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('Edit view') : t('Save current filter…')}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPending}
            data-testid="saved-view-save"
          >
            {isPending ? t('Saving…') : t('Save view')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="error" title={t('Failed to save view')}>
            {error}
          </Alert>
        )}
        <Input
          label={t('View name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('e.g. My in-progress items')}
          autoFocus
          required
          data-testid="saved-view-name"
        />
        <Checkbox
          checked={isShared}
          onChange={(e) => setIsShared(e.target.checked)}
          label={t('Share with the organization')}
          data-testid="saved-view-shared"
        />
      </div>
    </Modal>
  );
}
